import React, { useCallback, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Button,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MapIcon from '@mui/icons-material/Map';
import PaymentIcon from '@mui/icons-material/Payment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PeopleIcon from '@mui/icons-material/People';
import CelebrationIcon from '@mui/icons-material/Celebration';
import WebhookIcon from '@mui/icons-material/Webhook';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import EmailIcon from '@mui/icons-material/Email';
import MenuIcon from '@mui/icons-material/Menu';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import BoltIcon from '@mui/icons-material/Bolt';
import CategoryIcon from '@mui/icons-material/Category';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MainLayout from '../layout/MainLayout';
import FestivalSelector from '../common/FestivalSelector';
import StatsSidebar from './StatsSidebar';
import AdminVendorRegistration from './AdminVendorRegistration';
import BulkEnrollReturningVendors from './BulkEnrollReturningVendors';
import { SidebarActionsProvider, useSidebarActions } from './SidebarActionsContext';
import { useAuth } from '../../context/AuthContext';

const DRAWER_WIDTH = 280;
// Keep the right rail narrow so 1280–1500px screens still have a usable
// main column. Slightly wider on xl just so revenue/money formats breathe.
const RIGHT_DRAWER_WIDTH = { xs: 280, lg: 232, xl: 256 };

const OPS_NAV = [
  { to: '/admin', label: 'Dashboard', icon: <DashboardIcon />, end: true },
  { to: '/admin/approvals', label: 'Approvals', icon: <PendingActionsIcon /> },
  { to: '/admin/final-registrations', label: 'Final Registrations', icon: <AssignmentTurnedInIcon /> },
  { to: '/admin/vendors', label: 'Vendors', icon: <StorefrontIcon /> },
  { to: '/admin/categories', label: 'Categories', icon: <CategoryIcon /> },
  { to: '/admin/booths', label: 'Booths', icon: <MapIcon /> },
  { to: '/admin/payments', label: 'Manage Billing', icon: <PaymentIcon /> },
  { to: '/admin/registration-flow', label: 'Registration Flow', icon: <AccountTreeIcon /> },
];

const PLATFORM_NAV = [
  { to: '/admin/users', label: 'Users', icon: <PeopleIcon /> },
  { to: '/admin/festivals', label: 'Festivals', icon: <CelebrationIcon /> },
  { to: '/admin/map', label: 'Map Editor', icon: <EditLocationAltIcon /> },
  { to: '/admin/email-settings', label: 'Email Settings', icon: <EmailIcon /> },
  { to: '/admin/webhooks', label: 'Webhooks', icon: <WebhookIcon /> },
];

const NavList = ({ items, onItemClick }) => (
  <List dense disablePadding>
    {items.map((item) => (
      <ListItem key={item.to} disablePadding>
        <ListItemButton
          component={NavLink}
          to={item.to}
          end={item.end}
          onClick={onItemClick}
          sx={{
            borderRadius: 1,
            mx: 1,
            my: 0.25,
            '&.active': {
              backgroundColor: 'action.selected',
              fontWeight: 700,
              '& .MuiListItemText-primary': { fontWeight: 700 },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      </ListItem>
    ))}
  </List>
);

// Compact button styling shared by the quick-action row and per-page action
// list. Smaller font and tighter padding keep the right rail scannable.
const COMPACT_BUTTON_SX = {
  fontSize: '0.75rem',
  py: 0.4,
  px: 1,
  minHeight: 0,
  textTransform: 'none',
  fontWeight: 600,
  '& .MuiButton-startIcon': { mr: 0.5 },
  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 16 },
};

const CompactActionButton = ({ action, fullWidth = false }) => (
  <Button
    onClick={action.onClick}
    variant={action.variant || 'outlined'}
    color={action.color || 'primary'}
    startIcon={action.icon}
    disabled={action.disabled}
    size="small"
    fullWidth={fullWidth}
    sx={{
      ...COMPACT_BUTTON_SX,
      ...(fullWidth ? { justifyContent: 'flex-start' } : {}),
    }}
  >
    {action.label}
  </Button>
);

const QuickActionsPanel = () => {
  const { actions, openRegisterVendor, openImportReturning } = useSidebarActions();

  const globalActions = [
    {
      key: 'register-vendor',
      label: 'Add Vendor',
      icon: <PersonAddAlt1Icon />,
      variant: 'contained',
      onClick: openRegisterVendor,
    },
    {
      key: 'import-returning',
      label: 'Returning Vendors',
      icon: <GroupAddIcon />,
      variant: 'outlined',
      onClick: openImportReturning,
    },
  ];

  return (
    <Box sx={{ px: 1.5, py: 1.25 }}>
      <Stack direction="column" spacing={0.75}>
        {globalActions.map((a) => (
          <CompactActionButton key={a.key} action={a} fullWidth />
        ))}
      </Stack>

      {actions && actions.length > 0 ? (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
            On this page
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
            {actions.map((a, i) => (
              <CompactActionButton key={a.key || a.label || i} action={a} fullWidth />
            ))}
          </Box>
        </>
      ) : null}
    </Box>
  );
};

const LeftSidebarContents = ({ isSuperAdmin, onItemClick }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
        Festival
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        <FestivalSelector />
      </Box>
    </Box>
    <Divider />
    <Box sx={{ py: 1, flexShrink: 0 }}>
      <Typography variant="overline" color="text.secondary" sx={{ px: 2, fontWeight: 700 }}>
        Operations
      </Typography>
      <NavList items={OPS_NAV} onItemClick={onItemClick} />
    </Box>
    {isSuperAdmin && (
      <>
        <Divider />
        <Box sx={{ py: 1, flexShrink: 0 }}>
          <Typography variant="overline" color="text.secondary" sx={{ px: 2, fontWeight: 700 }}>
            Platform
          </Typography>
          <NavList items={PLATFORM_NAV} onItemClick={onItemClick} />
        </Box>
      </>
    )}
    <Box sx={{ flexGrow: 1 }} />
  </Box>
);

const RightSidebarContents = ({ isHomePage }) => {
  const { rightPanel } = useSidebarActions();
  if (rightPanel) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {rightPanel}
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <QuickActionsPanel />
      <Divider />
      <StatsSidebar hideReservations={isHomePage} />
      <Box sx={{ flexGrow: 1 }} />
    </Box>
  );
};

const AdminLayoutInner = ({
  registerOpen,
  setRegisterOpen,
  importReturningOpen,
  setImportReturningOpen,
  onVendorSaved,
}) => {
  const { isSuperAdmin } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // The right sidebar (stats + quick actions) was squeezing the main column
  // on common ~1024–1280px laptop widths. Promote it to permanent only at
  // lg+ and offer a toggle button below that.
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileRightOpen, setMobileRightOpen] = useState(false);
  const isHomePage = location.pathname === '/admin' || location.pathname === '/admin/';

  const handleNavClick = () => {
    if (!isDesktop) setMobileOpen(false);
  };

  return (
    <MainLayout>
      <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
        {isDesktop ? (
          <Drawer
            variant="permanent"
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                position: 'static',
                borderRight: '1px solid',
                borderColor: 'divider',
                background: '#fafbfc',
              },
            }}
            open
          >
            <LeftSidebarContents isSuperAdmin={isSuperAdmin} onItemClick={handleNavClick} />
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModelProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                background: '#fafbfc',
              },
            }}
          >
            <LeftSidebarContents isSuperAdmin={isSuperAdmin} onItemClick={handleNavClick} />
          </Drawer>
        )}

        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
          {(!isDesktop || !isWide) && (
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: !isDesktop ? 'space-between' : 'flex-end',
              }}
            >
              {!isDesktop && (
                <IconButton
                  color="primary"
                  onClick={() => setMobileOpen(true)}
                  aria-label="open admin sidebar"
                >
                  <MenuIcon />
                </IconButton>
              )}
              {!isWide && (
                <IconButton
                  color="primary"
                  onClick={() => setMobileRightOpen(true)}
                  aria-label="open admin quick actions"
                >
                  <BoltIcon />
                </IconButton>
              )}
            </Box>
          )}
          <Outlet key={location.pathname} />
        </Box>

        {isWide ? (
          <Drawer
            variant="permanent"
            anchor="right"
            sx={{
              width: RIGHT_DRAWER_WIDTH,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: RIGHT_DRAWER_WIDTH,
                boxSizing: 'border-box',
                position: 'static',
                borderLeft: '1px solid',
                borderColor: 'divider',
                background: '#fafbfc',
              },
            }}
            open
          >
            <RightSidebarContents isHomePage={isHomePage} />
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            anchor="right"
            open={mobileRightOpen}
            onClose={() => setMobileRightOpen(false)}
            ModelProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: { xs: 280, sm: 320 },
                boxSizing: 'border-box',
                background: '#fafbfc',
              },
            }}
          >
            <RightSidebarContents isHomePage={isHomePage} />
          </Drawer>
        )}
      </Box>

      <AdminVendorRegistration
        open={registerOpen}
        mode="create"
        onClose={(didSave) => {
          setRegisterOpen(false);
          if (didSave === true) onVendorSaved();
        }}
      />

      <BulkEnrollReturningVendors
        open={importReturningOpen}
        onClose={(didEnroll) => {
          setImportReturningOpen(false);
          if (didEnroll === true) onVendorSaved();
        }}
      />
    </MainLayout>
  );
};

const AdminLayout = () => {
  const [registerOpen, setRegisterOpen] = useState(false);
  const [importReturningOpen, setImportReturningOpen] = useState(false);
  const [vendorChangeNonce, setVendorChangeNonce] = useState(0);

  const openRegisterVendor = useCallback(() => setRegisterOpen(true), []);
  const openImportReturning = useCallback(() => setImportReturningOpen(true), []);
  const notifyVendorChanged = useCallback(() => setVendorChangeNonce((n) => n + 1), []);

  return (
    <SidebarActionsProvider
      openRegisterVendor={openRegisterVendor}
      openImportReturning={openImportReturning}
      notifyVendorChanged={notifyVendorChanged}
      vendorChangeNonce={vendorChangeNonce}
    >
      <AdminLayoutInner
        registerOpen={registerOpen}
        setRegisterOpen={setRegisterOpen}
        importReturningOpen={importReturningOpen}
        setImportReturningOpen={setImportReturningOpen}
        onVendorSaved={notifyVendorChanged}
      />
    </SidebarActionsProvider>
  );
};

export default AdminLayout;
